<template>
	<section
		v-editable="blok"
		class="bg-background-secondary py-4 sm:pt-20 sm:pb-14 text-body-secondary overflow-hidden"
	>
		<div class="container">
			<h2 class="typo-h1 mb-3 sm:mb-8">
				{{ blok.title }}
			</h2>
			<div class="rem:max-w-[630px] mb-3 sm:mb-10">
				<RichText :data="blok.description" />
			</div>

			<div ref="swiperEl" class="swiper !overflow-visible pl-0 md:!pl-80">
				<div class="swiper-wrapper flex flex-nowrap">
					<div
						v-for="item in blok.children"
						:key="item._uid"
						class="swiper-slide shrink-0 !w-[calc(100%/1-50px)] md:!w-[calc(100%/1.5)] xl:!w-[calc(100%/2.1-15px)]"
					>
						<StoryblokComponent
							:blok="item"
							@update:is-open="(newValue: boolean) => isOpen = newValue"
							@slide-data="handleSlideData"
						/>
					</div>
				</div>
			</div>

			<div v-if="swiperControls" class="flex justify-end mt-12">
				<div class="inline-flex items-center gap-4 border border-border-tertiary shadow-xs rounded-full bg-white">
					<button
						type="button"
						:class="`swiper-prev-${blok._uid}`"
						class="rounded-full bg-white text-icon-primary hover:bg-background-secondary disabled:bg-white disabled:text-body-disabled p-2"
					>
						<NuxtIcon name="chevron-left" filled class="text-2xl" />
					</button>
					{{ swiperIndex + 1 }} z {{ swiperLoop ? blok.children.length : swiperSlides }}
					<button
						type="button"
						:class="`swiper-next-${blok._uid}`"
						class="swiper-next rounded-full bg-white text-icon-primary hover:bg-background-secondary disabled:bg-white disabled:text-body-disabled p-2"
					>
						<NuxtIcon name="chevron-right" filled class="text-2xl" />
					</button>
				</div>
			</div>
		</div>
	</section>

	<ClientTeleport>
		<Transition name="fade">
			<Modal v-if="isOpen" :is-open="isOpen" @update:is-open="newValue => isOpen = newValue" />
		</Transition>
	</ClientTeleport>
</template>

<script setup lang="ts">
	import {useWindowSize} from '@vueuse/core';
	import {Swiper} from 'swiper';
	import {Navigation, Autoplay} from 'swiper/modules';
	import type {Swiper as SwiperClass, SwiperOptions} from 'swiper/types';
	import 'swiper/css';
	import 'swiper/css/navigation';
	import type {AssetStoryblok, CarouselWithDescriptionStoryblok} from '@/storyblok/types';

	interface Props {
		blok: CarouselWithDescriptionStoryblok;
	}

	const props = defineProps<Props>();

	const swiperEl = ref<HTMLElement | null>(null);
	const swiperInstance = shallowRef<SwiperClass | null>(null);
	const {width} = useWindowSize();

	const swiperIndex = ref(0);
	const swiperSlides = ref(props.blok.children.length);
	const isOpen = ref(false);
	const slide = ref<AssetStoryblok>();

	const swiperLoop = computed(() => (width.value >= 1200 && props.blok.children.length >= 6) || (width.value < 1200 && props.blok.children.length >= 4));
	const swiperControls = computed(() => (width.value >= 1200 && props.blok.children.length >= 3) || (width.value < 1200 && props.blok.children.length >= 2));

	const handleSlideData = (e: AssetStoryblok) => {
		slide.value = e;
	};

	const getSwiperConfig = (): SwiperOptions => ({
		modules: [Navigation, Autoplay],
		slidesPerView: 'auto',
		spaceBetween: 8,
		autoplay: {
			delay: 10000,
			disableOnInteraction: true,
		},
		breakpoints: {900: {spaceBetween: 24}},
		navigation: {
			nextEl: `.swiper-next-${props.blok._uid}`,
			prevEl: `.swiper-prev-${props.blok._uid}`,
		},
		loop: swiperLoop.value,
		on: {
			slideChange: (swiper) => {
				swiperSlides.value = swiper.snapGrid.length;
				swiperIndex.value = swiper.realIndex;
			},
		},
	});

	onMounted(async () => {
		await nextTick();

		if (swiperEl.value instanceof HTMLElement) {
			swiperInstance.value = new Swiper(swiperEl.value, getSwiperConfig());
		}
	});

	onBeforeUnmount(() => {
		swiperInstance.value?.destroy(true, true);
		swiperInstance.value = null;
	});

	watch(isOpen, (isModalOpen) => {
		if (!swiperInstance.value) {
			return;
		}

		if (isModalOpen) {
			swiperInstance.value.autoplay.pause();
			document.body.style.overflow = 'hidden';
		} else {
			swiperInstance.value.autoplay.resume();
			document.body.style.overflow = '';
		}
	});
</script>
